import React, { useState, useEffect } from 'react';
import { FaPlus, FaMinus, FaTrash, FaHeart } from 'react-icons/fa';
import './cartpage.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/authContext';
import { checkAuth } from '../../utils/auth';
import Loader from '../loader';
import PurchaseModal from '../purchaseModal/PurchaseModal';

const API = process.env.REACT_APP_API_URL;

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  const { cart, fetchCart, wishlist, setWishlist, fetchWishlist, user } = useAuth();
  const navigate = useNavigate();

  // Fetch wishlist on mount
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Initial load: guest or logged-in
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      fetchCart();
    } else {
      const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
      if (guestCart.length === 0) {
        setCartItems([]);
      } else {
        const mappedGuestCart = guestCart.map(item => ({
          ...item,
          id: item.product_id,
          image: item.image || '/placeholder.jpg',
          inStock: item.in_stock ?? true,
          quantity: item.quantity || 1
        }));
        setCartItems(mappedGuestCart);
      }
      setLoading(false);
    }
  }, []);

  // Sync cart from context
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && Array.isArray(cart)) {
      const mappedCart = cart.map(item => ({
        ...item,
        id: item.product_id,
        image: item.image || '/placeholder.jpg',
        inStock: item.in_stock ?? true,
        quantity: item.quantity || 1
      }));
      setCartItems(mappedCart);
      setLoading(false);
    }
  }, [cart]);

  // Wishlist toggle
 const handleToggleWishlist = async (product) => {
  if (!checkAuth()) {
    toast.error("Session expired. Please login to continue.");
    return navigate(`/auth?tab=register`);
  }

  const token = localStorage.getItem('token');
  const isInWishlist = wishlist.some(item => Number(item.product_id) === Number(product.product_id));

  const bodyPayload = isInWishlist
    ? { product_id: product.product_id, category: product.category }
    : {
        product_id: product.product_id,
        category: product.category,
        name: product.name,
        price: product.price,
        image_url: product.image || "/placeholder.jpg",
        in_stock: product.inStock ?? true
      };

  console.log("Wishlist payload:", bodyPayload);

  try {
    const url = isInWishlist 
      ? `${API}/api/wishlist/remove` 
      : `${API}/api/wishlist/add`;

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(bodyPayload)
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Failed to update wishlist: ${errText}`);
    }

    if (isInWishlist) {
      setWishlist(prev => prev.filter(item => Number(item.product_id) !== Number(product.product_id)));
      toast.error('Removed from wishlist 💔');
    } else {
      setWishlist(prev => [...prev, { product_id: product.product_id, category: product.category }]);
      toast.success('Added to wishlist ❤️');
    }
  } catch (err) {
    console.error(err);
    toast.error("Wishlist update failed");
  }
};

  // Quantity update
  const updateQuantity = (id, change) => {
    const token = localStorage.getItem("token");
    const itemToUpdate = cartItems.find(item => item.id === id);
    if (!itemToUpdate) return;

    const newQuantity = Number(itemToUpdate.quantity) + change;

    if (newQuantity < 1) {
      const updatedItems = cartItems.filter(item => item.id !== id);
      setCartItems(updatedItems);

      if (token) {
        fetch(`${API}/api/cart/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ product_id: id, quantity: 0, category: itemToUpdate.category })
        });
      } else {
        const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
        const newGuestCart = guestCart.filter(item => item.product_id !== id);
        localStorage.setItem("cart", JSON.stringify(newGuestCart));
      }
      return;
    }

    const updatedCart = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedCart);

    if (token) {
      fetch(`${API}/api/cart/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_id: id, quantity: newQuantity, category: itemToUpdate.category })
      });
    } else {
      const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
      const newGuestCart = guestCart.map(item =>
        item.product_id === id ? { ...item, quantity: newQuantity } : item
      );
      localStorage.setItem("cart", JSON.stringify(newGuestCart));
    }
  };

  // Remove item
  const handleRemoveItem = async (id) => {
    const token = localStorage.getItem('token');
    const itemToRemove = cartItems.find(item => item.id === id);
    if (!token || !itemToRemove) return;

    try {
      await fetch(`${API}/api/cart/remove`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ product_id: id, category: itemToRemove.category })
      });
      toast.success("Product removed");
      fetchCart();
    } catch (error) {
      console.error(error);
    }
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  // Subtotal & total
  const subtotal = cartItems.reduce((sum, item) => sum + Number(item.price) * Number(item.quantity), 0);
  const totalAmount = subtotal;

  if (loading) return <Loader />;

  return (
    <div className="cart-container">
      {/* Cart Items List */}
      <div className="items-list-container">
        <div className="items-list-header">
          <h2>Your Items ({cartItems.length})</h2>
        </div>
        <div className="cart-items-wrapper">
          {cartItems.length === 0 ? (
            <div className="empty-cart-message">Your cart is empty 😢</div>
          ) : (
            cartItems.map(item => {
              const isInWishlist = wishlist && wishlist.some(w => Number(w.product_id) === Number(item.id));

              return (
                <div key={item.id + '-' + item.category} className="cart-item">
                  <div
                    className="item-image-container cursor-pointer"
                    onClick={() => navigate(`/product/${item.id}`)}
                  >
                    <img
                      src={`${API}${item.image}`}
                      alt={item.name}
                      className="item-image"
                    />
                  </div>
                  <div
                    className="item-info cursor-pointer"
                    onClick={() => navigate(`/product/${item.id}`)}
                  >
                    <h5 className="item-name hover:underline">{item.name}</h5>
                    <p className="item-price">₹{Number(item.price).toFixed(2)}</p>
                    <p className={`item-stock ${item.inStock ? 'in-stock' : 'out-of-stock'}`}>
                      {item.inStock ? 'In Stock' : 'Out of Stock'}
                    </p>
                  </div>
                  <div className="item-actions">
                    <div className="quantity-control">
                      <button className="quantity-btn" onClick={() => updateQuantity(item.id, -1)}>
                        <FaMinus />
                      </button>
                      <span className="quantity-display">{item.quantity}</span>
                      <button className="quantity-btn" onClick={() => updateQuantity(item.id, +1)}>
                        <FaPlus />
                      </button>
                    </div>
                    <div className="action-buttons">
          <button
            className="action-btn wishlist-btn"
            onClick={() =>
              handleToggleWishlist({
                product_id: item.id,
                name: item.name,
                price: item.price,
                image: item.image,
                inStock: item.inStock,
                category: item.category || "default"
              })
            }
            style={{
              color: isInWishlist ? '#f43f5e' : '#999', // red if in wishlist, grey if not
              borderColor: isInWishlist ? '#f43f5e' : '#ccc',
              backgroundColor: isInWishlist ? '#fff1f2' : '#fff'
            }}
          >
            <FaHeart />
          </button>

          <button
            className="action-btn remove-btn"
            onClick={() => handleRemoveItem(item.id)}
          >
            <FaTrash />
          </button>
        </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Order Summary */}
      <div className="order-summary">
        <h3 className="summary-title">Order Summary</h3>
        <div className="summary-line">
          <span>Subtotal</span>
          <span>₹{subtotal.toFixed(2)}</span>
        </div>
        <hr className="summary-divider" />
        <div className="summary-total">
          <span>Total</span>
          <span>₹{totalAmount.toFixed(2)}</span>
        </div>

        <button
          className="btn-proceed"
          onClick={() => {
            const token = localStorage.getItem("token");
            if (!token) {
              toast.error("Session expired. Please login to proceed with payment");
              navigate('/auth?tab=register');
            } else {
              setIsPurchaseModalOpen(true);
            }
          }}
          disabled={cartItems.length === 0}
        >
          PROCEED TO PAYMENT
        </button>

        <p className="secure-payment-info">Secure payments via RazorPay</p>
      </div>

      {/* Purchase Modal */}
      {isPurchaseModalOpen && (
        <PurchaseModal 
          user={user} 
          items={cartItems} 
          subtotal={totalAmount}
          checkoutType="cart"
          onClose={() => setIsPurchaseModalOpen(false)} 
        />
      )}
    </div>
  );
};

export default CartPage;